import type { ReceivingQueue, ReceivingQueueReceipt } from "../gateway/receiving-client";
import type {
  PosOrderDetailResponse,
  PosReceivableBusinessState,
  PosSalesHistoryOrder,
  PosSalesHistoryPayment,
  PosSalesHistoryResponse,
} from "../gateway/sales-history-client";
import type { ShiftCloseRuntime } from "../gateway/shift-close-client";
import { indexedScope, normalizeIdentifier } from "./indexed-runtime";
import { posRuntimeDb, type JournalRecord, type OutboxRecord } from "./pos-db";
import type { TrustedTerminalIdentity } from "./terminal-state";

function clean(value: string | null | undefined) {
  return (value ?? "").trim().toLocaleLowerCase("th").replace(/\s+/g, " ");
}

function tokens(...values: Array<string | null | undefined>) {
  const result = new Set<string>();
  for (const raw of values) {
    const value = clean(raw);
    if (!value) continue;
    result.add(value);
    for (const part of value.split(/[^\p{L}\p{N}@._+-]+/u).filter(Boolean)) result.add(part);
    const compact = normalizeIdentifier(value);
    if (compact) result.add(compact);
  }
  return [...result];
}

function saleTransactionScope(identity: TrustedTerminalIdentity) {
  return `org:${identity.organization_id}:branch:${identity.branch_id}:node:${identity.node_id}`;
}

type LocalHistoryLine = {
  lineId: string;
  menuName: string;
  variantLabel: string;
  quantity: number;
  unitPrice: number;
};

type LocalHistoryMember = {
  customerId: string;
  membershipId: string;
  customerCode: string;
  customerName: string;
};

type LocalCanonicalPayment = {
  payment_id: string;
  payment_status: string;
  payment_method_code: string;
  payment_method_name: string;
  amount: number;
  tendered_amount?: number | null;
  change_amount?: number | null;
  captured_at?: string | null;
  settled_at?: string | null;
  voided_at?: string | null;
};

type LocalCanonicalSale = {
  order_id: string;
  order_no: string;
  order_status: string;
  currency_code: string;
  grand_total: number;
  payment?: LocalCanonicalPayment | null;
};

type LocalSaleHistorySnapshot = {
  schema: "LOCAL_CASH_SALE_V1" | "LOCAL_NONCASH_SALE_V1" | "LOCAL_CREDIT_SALE_V1";
  localTransactionId: string;
  rootRequestKey: string;
  performingEmployeeId: string;
  lines: LocalHistoryLine[];
  selectedMember?: LocalHistoryMember | null;
  localTotal: number;
  currencyCode: string;
  localCommittedAt: string;
  paymentMethodCode?: string;
  paymentMethodName?: string;
  tenderedAmount?: number;
  changeAmount?: number;
  canonical?: LocalCanonicalSale | null;
};

function localSaleSnapshot(record: JournalRecord): LocalSaleHistorySnapshot | null {
  if (record.kind !== "SALE_ORDER" || !record.snapshot || typeof record.snapshot !== "object") return null;
  const snapshot = record.snapshot as Partial<LocalSaleHistorySnapshot>;
  if (!["LOCAL_CASH_SALE_V1", "LOCAL_NONCASH_SALE_V1", "LOCAL_CREDIT_SALE_V1"].includes(snapshot.schema ?? "")) return null;
  if (
    !snapshot.localTransactionId ||
    !snapshot.rootRequestKey ||
    !snapshot.performingEmployeeId ||
    !Array.isArray(snapshot.lines) ||
    typeof snapshot.localTotal !== "number" ||
    !Number.isFinite(snapshot.localTotal) ||
    !snapshot.currencyCode ||
    !snapshot.localCommittedAt
  ) return null;
  return snapshot as LocalSaleHistorySnapshot;
}

function localBusinessDate(timestamp: string, timezone: string) {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(date);
    const year = parts.find((part) => part.type === "year")?.value;
    const month = parts.find((part) => part.type === "month")?.value;
    const day = parts.find((part) => part.type === "day")?.value;
    return year && month && day ? `${year}-${month}-${day}` : "";
  } catch {
    return "";
  }
}

function localSaleStatus(snapshot: LocalSaleHistorySnapshot, outbox: OutboxRecord | undefined) {
  if (snapshot.canonical?.order_status) return snapshot.canonical.order_status;
  if (outbox?.state === "SENDING") return "กำลังดำเนินการ";
  if (outbox?.state === "FAILED") return "รอดำเนินการอีกครั้ง";
  return "รอดำเนินการ";
}

function localPayment(snapshot: LocalSaleHistorySnapshot): PosSalesHistoryPayment[] {
  const canonicalPayment = snapshot.canonical?.payment;
  if (canonicalPayment) {
    return [{
      payment_id: canonicalPayment.payment_id,
      payment_status: canonicalPayment.payment_status,
      payment_method_code: canonicalPayment.payment_method_code,
      payment_method_name: canonicalPayment.payment_method_name,
      amount: Number(canonicalPayment.amount),
      tendered_amount: canonicalPayment.tendered_amount ?? null,
      change_amount: canonicalPayment.change_amount ?? null,
      captured_at: canonicalPayment.captured_at ?? null,
      settled_at: canonicalPayment.settled_at ?? null,
      voided_at: canonicalPayment.voided_at ?? null,
    }];
  }

  if (snapshot.schema === "LOCAL_CREDIT_SALE_V1" || snapshot.localTotal === 0) return [];
  if (snapshot.schema === "LOCAL_CASH_SALE_V1") {
    return [{
      payment_id: `local:${snapshot.localTransactionId}:payment`,
      payment_status: "บันทึกแล้ว",
      payment_method_code: "CASH",
      payment_method_name: "เงินสด",
      amount: snapshot.localTotal,
      tendered_amount: snapshot.tenderedAmount ?? snapshot.localTotal,
      change_amount: snapshot.changeAmount ?? 0,
      captured_at: snapshot.localCommittedAt,
      settled_at: null,
      voided_at: null,
    }];
  }

  return [{
    payment_id: `local:${snapshot.localTransactionId}:payment`,
    payment_status: "บันทึกแล้ว",
    payment_method_code: snapshot.paymentMethodCode ?? "NONCASH",
    payment_method_name: snapshot.paymentMethodName ?? "สแกน / โอน",
    amount: snapshot.localTotal,
    tendered_amount: null,
    change_amount: null,
    captured_at: snapshot.localCommittedAt,
    settled_at: null,
    voided_at: null,
  }];
}

function localReceivable(snapshot: LocalSaleHistorySnapshot): PosReceivableBusinessState | null {
  if (snapshot.schema !== "LOCAL_CREDIT_SALE_V1" || snapshot.canonical) return null;
  return {
    receivable_id: `local:${snapshot.localTransactionId}:receivable`,
    receivable_no: "รอยืนยัน",
    receivable_status: "OPEN",
    currency_code: snapshot.currencyCode,
    original_amount: snapshot.localTotal,
    allocated_amount: 0,
    remaining_amount: snapshot.localTotal,
    due_date: "",
    created_at: snapshot.localCommittedAt,
    updated_at: snapshot.localCommittedAt,
    paid_at: null,
    voided_at: null,
  };
}

function localOrder(
  snapshot: LocalSaleHistorySnapshot,
  outbox: OutboxRecord | undefined,
  timezone: string,
): PosSalesHistoryOrder {
  const canonical = snapshot.canonical;
  const member = snapshot.selectedMember;
  return {
    order_id: canonical?.order_id ?? snapshot.localTransactionId,
    order_no: canonical?.order_no ?? `รายการ ${snapshot.localTransactionId.slice(0, 8).toUpperCase()}`,
    business_date: localBusinessDate(snapshot.localCommittedAt, timezone),
    transaction_at: snapshot.localCommittedAt,
    order_status: localSaleStatus(snapshot, outbox),
    currency_code: canonical?.currency_code ?? snapshot.currencyCode,
    grand_total: canonical?.grand_total ?? snapshot.localTotal,
    refunded_amount: 0,
    source_type: canonical ? "POS" : "POS_LOCAL",
    is_test: false,
    performing_employee: { employee_id: snapshot.performingEmployeeId },
    customer: member ? {
      customer_id: member.customerId,
      membership_id: member.membershipId,
      customer_name: member.customerName,
      customer_code: member.customerCode,
    } : {},
    items: snapshot.lines.map((line) => ({
      line_id: line.lineId,
      item_name: line.menuName,
      variant_name: line.variantLabel || null,
      quantity: line.quantity,
      line_total: Number((line.unitPrice * line.quantity).toFixed(2)),
    })),
    payments: localPayment(snapshot),
    receivable: localReceivable(snapshot),
  };
}

async function readLocalSaleOrders(identity: TrustedTerminalIdentity, timezone: string) {
  const transactionScope = saleTransactionScope(identity);
  const journal = await posRuntimeDb.journal
    .where("scopeKey")
    .equals(transactionScope)
    .filter((record) => record.kind === "SALE_ORDER")
    .toArray();
  const snapshots = journal
    .map((record) => ({ record, snapshot: localSaleSnapshot(record) }))
    .filter((item): item is { record: JournalRecord; snapshot: LocalSaleHistorySnapshot } => Boolean(item.snapshot));
  const outboxes = await Promise.all(snapshots.map((item) => posRuntimeDb.outbox.get(item.snapshot.rootRequestKey)));
  return snapshots.map((item, index) => ({
    order: localOrder(item.snapshot, outboxes[index], timezone),
    canonicalOrderId: item.snapshot.canonical?.order_id ?? null,
    pending: !item.snapshot.canonical,
  }));
}

function fallbackHistoryMeta(identity: TrustedTerminalIdentity, timezone: string, businessDate: string): Omit<PosSalesHistoryResponse, "orders" | "pagination"> {
  return {
    ok: true,
    contract: "GET_POS_SALES_HISTORY_V1",
    timezone,
    business_date: businessDate,
    organization_id: identity.organization_id,
    branch_id: identity.branch_id,
    node_id: identity.node_id,
    employee_id: "",
    permissions: {
      sales_history_visible: true,
      cash_void_candidate: false,
      credit_void_candidate: false,
    },
  };
}

export async function saveReceivingQueue(identity: TrustedTerminalIdentity, queue: ReceivingQueue) {
  const scopeKey = indexedScope(identity, "receiving");
  await posRuntimeDb.transaction("rw", posRuntimeDb.receivingQueue, posRuntimeDb.runtimeMeta, async () => {
    const incoming = new Set(queue.receipts.map((receipt) => receipt.receipt_id));
    const existing = await posRuntimeDb.receivingQueue.where("scopeKey").equals(scopeKey).toArray();
    await posRuntimeDb.receivingQueue.bulkDelete(existing.filter((row) => !incoming.has(row.receiptId)).map((row) => row.key));
    for (const receipt of queue.receipts) {
      await posRuntimeDb.receivingQueue.put({
        key: `${scopeKey}:receipt:${receipt.receipt_id}`,
        scopeKey,
        receiptId: receipt.receipt_id,
        status: receipt.receiving_status,
        searchTokens: tokens(
          receipt.internal_bill_no,
          receipt.document_ref,
          receipt.supplier_name,
          ...receipt.lines.flatMap((line) => [line.sku_code, line.sku_name]),
        ),
        updatedAt: queue.generated_at,
        payload: receipt,
      });
    }
    await posRuntimeDb.runtimeMeta.put({
      key: `${scopeKey}:meta`,
      scopeKey,
      value: { ...queue, receipts: [] },
      updatedAt: queue.generated_at,
    });
  });
}

export async function readReceivingQueue(identity: TrustedTerminalIdentity): Promise<ReceivingQueue | null> {
  const scopeKey = indexedScope(identity, "receiving");
  const [meta, rows] = await Promise.all([
    posRuntimeDb.runtimeMeta.get(`${scopeKey}:meta`),
    posRuntimeDb.receivingQueue.where("scopeKey").equals(scopeKey).toArray(),
  ]);
  if (!meta?.value || typeof meta.value !== "object") return null;
  return { ...(meta.value as Omit<ReceivingQueue, "receipts">), receipts: rows.map((row) => row.payload as ReceivingQueueReceipt) };
}

export async function searchReceivingQueue(identity: TrustedTerminalIdentity, searchText: string, limit = 100): Promise<ReceivingQueueReceipt[]> {
  const scopeKey = indexedScope(identity, "receiving");
  const query = clean(searchText);
  if (!query) return (await posRuntimeDb.receivingQueue.where("scopeKey").equals(scopeKey).limit(limit).toArray()).map((row) => row.payload as ReceivingQueueReceipt);
  const rows = await posRuntimeDb.receivingQueue.where("searchTokens").startsWithIgnoreCase(query).and((row) => row.scopeKey === scopeKey).limit(limit).toArray();
  return rows.map((row) => row.payload as ReceivingQueueReceipt);
}

export async function saveSalesHistory(identity: TrustedTerminalIdentity, response: PosSalesHistoryResponse) {
  const scopeKey = indexedScope(identity, "sales-history");
  await posRuntimeDb.transaction("rw", posRuntimeDb.salesHistory, posRuntimeDb.runtimeMeta, async () => {
    for (const order of response.orders) {
      await posRuntimeDb.salesHistory.put({
        key: `${scopeKey}:order:${order.order_id}`,
        scopeKey,
        orderId: order.order_id,
        businessDate: order.business_date,
        status: order.order_status,
        orderedAt: order.transaction_at,
        searchTokens: tokens(
          order.order_no,
          order.customer?.customer_name,
          order.customer?.customer_code,
          order.performing_employee?.employee_name,
          order.performing_employee?.employee_code,
          ...order.items.flatMap((item) => [item.item_name, item.variant_name]),
          ...order.payments.flatMap((payment) => [payment.payment_method_code, payment.payment_method_name]),
        ),
        updatedAt: new Date().toISOString(),
        payload: order,
      });
    }
    await posRuntimeDb.runtimeMeta.put({
      key: `${scopeKey}:meta`,
      scopeKey,
      value: { ...response, orders: [] },
      updatedAt: new Date().toISOString(),
    });
  });
}

export async function readSalesHistory(identity: TrustedTerminalIdentity, businessDate?: string | null, searchText?: string | null, limit = 50, offset = 0): Promise<PosSalesHistoryResponse | null> {
  const scopeKey = indexedScope(identity, "sales-history");
  const meta = await posRuntimeDb.runtimeMeta.get(`${scopeKey}:meta`);
  const cachedBase = meta?.value && typeof meta.value === "object"
    ? meta.value as Omit<PosSalesHistoryResponse, "orders" | "pagination"> & { pagination?: PosSalesHistoryResponse["pagination"] }
    : null;
  const timezone = cachedBase?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const localOrders = await readLocalSaleOrders(identity, timezone);
  const canonicalRows = await posRuntimeDb.salesHistory.where("scopeKey").equals(scopeKey).toArray();
  if (!cachedBase && !canonicalRows.length && !localOrders.length) return null;

  const canonicalOrderIds = new Set(canonicalRows.map((row) => row.orderId));
  const merged: PosSalesHistoryOrder[] = canonicalRows.map((row) => row.payload as PosSalesHistoryOrder);
  for (const local of localOrders) {
    if (local.canonicalOrderId && canonicalOrderIds.has(local.canonicalOrderId)) continue;
    merged.push(local.order);
  }

  const requestedBusinessDate = businessDate?.trim() || null;
  const query = clean(searchText);
  let filtered = requestedBusinessDate ? merged.filter((order) => order.business_date === requestedBusinessDate) : merged;
  if (query) {
    filtered = filtered.filter((order) => tokens(
      order.order_no,
      order.customer?.customer_name,
      order.customer?.customer_code,
      order.performing_employee?.employee_name,
      order.performing_employee?.employee_code,
      ...order.items.flatMap((item) => [item.item_name, item.variant_name]),
      ...order.payments.flatMap((payment) => [payment.payment_method_code, payment.payment_method_name]),
    ).some((token) => token.startsWith(query)));
  }
  filtered.sort((a, b) => (b.transaction_at ?? "").localeCompare(a.transaction_at ?? ""));
  const sliced = filtered.slice(offset, offset + limit);
  const fallbackDate = requestedBusinessDate
    || localBusinessDate(new Date().toISOString(), timezone)
    || cachedBase?.business_date
    || "";
  const base = cachedBase ?? fallbackHistoryMeta(identity, timezone, fallbackDate);
  const hasPendingLocalSale = localOrders.some((item) => item.pending);

  return {
    ...base,
    business_date: requestedBusinessDate || base.business_date || fallbackDate,
    permissions: hasPendingLocalSale ? {
      ...base.permissions,
      // A locally committed sale has no canonical void capability yet. Disable
      // candidate void actions for this merged page until Owner ACK/read-back.
      cash_void_candidate: false,
      credit_void_candidate: false,
    } : base.permissions,
    pagination: { total_count: filtered.length, limit, offset, returned_count: sliced.length },
    orders: sliced,
  } as PosSalesHistoryResponse;
}

export async function saveSalesOrderDetail(identity: TrustedTerminalIdentity, detail: PosOrderDetailResponse) {
  const scopeKey = indexedScope(identity, "sales-history");
  await posRuntimeDb.salesOrderDetails.put({
    key: `${scopeKey}:detail:${detail.order.order_id}`,
    scopeKey,
    orderId: detail.order.order_id,
    updatedAt: new Date().toISOString(),
    payload: detail,
  });
}

export async function readSalesOrderDetail(identity: TrustedTerminalIdentity, orderId: string): Promise<PosOrderDetailResponse | null> {
  const scopeKey = indexedScope(identity, "sales-history");
  const row = await posRuntimeDb.salesOrderDetails.get(`${scopeKey}:detail:${orderId}`);
  return row?.payload && typeof row.payload === "object" ? row.payload as PosOrderDetailResponse : null;
}

export async function saveShiftCloseRuntime(identity: TrustedTerminalIdentity, runtime: ShiftCloseRuntime) {
  const scopeKey = indexedScope(identity, "shift-close");
  await posRuntimeDb.shiftRuntime.put({
    key: `${scopeKey}:runtime`,
    scopeKey,
    updatedAt: new Date().toISOString(),
    payload: runtime,
  });
}

export async function readShiftCloseRuntime(identity: TrustedTerminalIdentity): Promise<ShiftCloseRuntime | null> {
  const scopeKey = indexedScope(identity, "shift-close");
  const row = await posRuntimeDb.shiftRuntime.get(`${scopeKey}:runtime`);
  return row?.payload && typeof row.payload === "object" ? row.payload as ShiftCloseRuntime : null;
}
